import { Link } from "react-router-dom";
import { formatReportDateTime } from "../../hooks/useReportSnapshot.js";
import { NAVY } from "./reportNavyTokens.js";

const IDAXIS_LOGO_DARKBG =
  "https://idaxis.ai/wp-content/uploads/2026/02/cropped-idaxis-logo-darkbg.png";

/**
 * Shared report page header — logo routes to workspace; company link lives in footer.
 */
export function ReportPageHeader({
  projectId,
  projectName,
  snapshotTakenAt,
  variant = "navy",
  showBackLink = true,
}) {
  const isNavy = variant === "navy";
  const workspacePath = `/project/${encodeURIComponent(projectId)}`;
  const muted = isNavy ? NAVY.mut : "var(--idaxis-print-muted, #334155)";
  const accent = isNavy ? NAVY.elec : "var(--accent, #2fc9ab)";
  const label = projectName ? `← ${projectName}` : "← Back to workspace";

  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <Link to={workspacePath} title="Back to workspace" style={{ display: "inline-flex", lineHeight: 0 }}>
          <img src={IDAXIS_LOGO_DARKBG} alt="idwiz workspace" style={{ height: 28, width: "auto" }} />
        </Link>
        {showBackLink ? (
          <Link
            to={workspacePath}
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: accent,
              textDecoration: "none",
            }}
          >
            {label}
          </Link>
        ) : null}
      </div>
      <div style={{ fontSize: 12, color: muted }}>{formatReportDateTime(snapshotTakenAt)}</div>
    </header>
  );
}

/**
 * Shared report page footer — workspace link + copyright.
 */
export function ReportPageFooter({
  projectId,
  projectName,
  providerLabel,
  snapshots = [],
  activeSnapshotId,
  onSnapshotChange,
  variant = "navy",
}) {
  const isNavy = variant === "navy";
  const muted = isNavy ? NAVY.mut : "var(--idaxis-print-muted, #334155)";
  const faint = isNavy ? NAVY.faint : "var(--idaxis-print-muted, #64748b)";
  const accent = isNavy ? NAVY.elec : "var(--accent, #2fc9ab)";
  const workspacePath = `/project/${encodeURIComponent(projectId)}`;
  const backLabel = projectName ? `← ${projectName}` : "← Back to workspace";

  return (
    <footer
      style={{
        marginTop: 32,
        paddingTop: 20,
        borderTop: `1px solid ${isNavy ? NAVY.line : "var(--idaxis-print-border, #e5e7eb)"}`,
        display: "grid",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Link to={workspacePath} style={{ fontSize: 12, color: accent, textDecoration: "none", fontWeight: 600 }}>
          {backLabel}
        </Link>
        <div style={{ fontSize: 12, color: faint }}>
          {providerLabel || "—"}
          {snapshots?.length ? (
            <span style={{ marginLeft: 12 }}>
              Snapshot:{" "}
              <select
                value={activeSnapshotId || ""}
                onChange={(e) => onSnapshotChange?.(e.target.value)}
                style={{
                  background: isNavy ? NAVY.panel : "var(--idaxis-print-bg, #fff)",
                  color: isNavy ? NAVY.fg : "var(--idaxis-print-text, #0b1b34)",
                  border: `1px solid ${isNavy ? NAVY.line : "var(--idaxis-print-border, #e5e7eb)"}`,
                  borderRadius: 4,
                  padding: "2px 6px",
                }}
              >
                {snapshots.map((s, idx) => (
                  <option key={s.snapshotId || idx} value={String(s.snapshotId)}>
                    {idx === 0 ? "Latest" : "Snapshot"} — {formatReportDateTime(s.takenAt)}
                  </option>
                ))}
              </select>
            </span>
          ) : null}
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 11, color: muted }}>
        © {new Date().getFullYear()} idaxis ·{" "}
        <a href="https://idaxis.ai" target="_blank" rel="noreferrer" style={{ color: accent, textDecoration: "none" }}>
          idaxis.ai
        </a>
      </p>
    </footer>
  );
}
